/**
 * PostCard Component
 * 
 * Displays a single post in the Team Feed with:
 * - Author info and timestamp
 * - Rich text content, GIFs, images, videos, voice memos
 * - Reactions and comments
 * - Edit/delete functionality
 * 
 * Extracted from TeamCommsApp.js for maintainability.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDesign } from '@/contexts/DesignContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { UserAvatar } from '@/components/shared';
import RichTextEditor from '@/components/shared/RichTextEditor';
import AttachmentMenu from '@/components/shared/AttachmentMenu';
import AttachmentViewer from '@/components/shared/AttachmentViewer';
import EmojiPickerButton from '@/components/shared/EmojiPickerButton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Send, 
  MoreHorizontal,
  Pin,
  Edit,
  Trash2,
  MessageSquare,
  X,
  Play,
  Mic,
  Cake,
  Eye,
  Smile,
  Loader2,
  BookOpen,
  ExternalLink,
  FileText,
  Download,
  Heart
} from 'lucide-react';
import { formatTimeAgo } from '../../../utils/timeFormat';
import { sanitizeHtml, applyMentionStyling, escapeHtml, normalizePastedSpaces } from '../../../utils/sanitize';

// Import extracted components
import { 
  VoiceMemoPlayer, 
  PostImageGallery, 
  PollDisplay,
  LoomEmbed
} from './index';

import VideoPlayer from '@/components/shared/VideoPlayer';
import AudioPlayer from '@/components/shared/AudioPlayer';
import { uploadWithProgress, uploadVideoWithProgress } from '@/lib/uploadVideoWithProgress';
import UploadProgress from '@/components/shared/UploadProgress';

import { GifPickerDialog } from './GifPicker';
import { getMentionMembers, getMentionGroups, peekMembers, peekGroups } from '@/lib/mentionDataCache';
import MentionSuggestions from '@/components/shared/MentionSuggestions';

// Import shared utilities
import { 
  API_URL, 
  REACTIONS,
  REACTION_OPTIONS,
  getReactionEmoji,
  isLegacyReactionKey
} from '../utils/constants';
import ReactionPicker from '@/components/shared/ReactionPicker';
import { IMAGE_ACCEPT, isImageFile } from '@/lib/imageFiles';

function studioPostTime(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return `Today, ${time}`;
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday, ${time}`;
  if ((now - d) / 86400000 < 7) return `${d.toLocaleDateString('en-US', { weekday: 'long' })}, ${time}`;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
}

function PostCard({ post, user, onUpdate, isHighlighted, highlightedCommentId, autoExpandComments }) {
  const { isStudio } = useDesign();
  // 104: the mockup draws a post's comments expanded, so Studio opens them for
  // posts that have any (classic keeps them collapsed).
  const [showComments, setShowComments] = useState(
    autoExpandComments || (isStudio && (post.comment_count || 0) > 0) || false
  );
  const [showAllComments, setShowAllComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null); // { commentId, userId, userName }
  const [showReactions, setShowReactions] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [removeGif, setRemoveGif] = useState(false);
  const [removeEmbed, setRemoveEmbed] = useState(false);
  const [removeVoiceMemo, setRemoveVoiceMemo] = useState(false);
  const [removeVideo, setRemoveVideo] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [showDeleteCommentDialog, setShowDeleteCommentDialog] = useState(false);
  const [isReacting, setIsReacting] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);
  const [isPinning, setIsPinning] = useState(false);
  const [localReactions, setLocalReactions] = useState(null); // For optimistic updates
  const [localReactionNames, setLocalReactionNames] = useState(null); // Names merged in realtime so the who-reacted tooltip never shows "User"
  const [commentReactionPopover, setCommentReactionPopover] = useState(null); // Track which comment's reaction popover is open
  const [commentGif, setCommentGif] = useState(null);
  // Image attachments on comments. Capped client-side at 4 (matches backend
  // Pydantic max_length=10 with headroom; comments shouldn't be galleries).
  const [commentImages, setCommentImages] = useState([]);  // [{ url, filename, size }]
  const [uploadingCommentImages, setUploadingCommentImages] = useState(false);
  const [commentImageProgress, setCommentImageProgress] = useState(null);
  const [showCommentGifPicker, setShowCommentGifPicker] = useState(false);
  const [showReadBy, setShowReadBy] = useState(false);
  const [readByData, setReadByData] = useState(null); // { seen: [], not_seen: [] }
  const [readByLoading, setReadByLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [commentMentions, setCommentMentions] = useState([]);
  // @-mention data — backed by session-scoped module cache to dedupe across PostCards.
  // Initialize from cache (sync peek) so subsequent composers in the same session
  // open with data already loaded — no flicker.
  const [churchMembers, setChurchMembers] = useState(() => peekMembers(user?.church_id));
  const [churchGroups, setChurchGroups] = useState(() => peekGroups(user?.church_id));
  const commentHighlightRef = useRef(null);
  const commentInputRef = useRef(null);
  const commentImageInputRef = useRef(null);
  const commentVideoInputRef = useRef(null);
  const [commentVideo, setCommentVideo] = useState(null);
  const [commentVideoProgress, setCommentVideoProgress] = useState(null);
  const mentionSuggestionsRef = useRef(null);

  // Check if this is a birthday post (post_type field, with fallback to emoji for old posts)
  const isBirthdayPost = post.post_type === 'birthday' || post.content?.startsWith('🎂');
  // Check if this is a devotion post
  const isDevotionPost = post.post_type === 'devotion';

  const isOwner = post.user_id === user.id;
  const isAdmin = user.role === 'admin' || user.role === 'owner';
  const canDelete = isOwner || isAdmin; // Admin can delete any post
  
  // Use local reactions if available (optimistic update), otherwise use post reactions
  const reactions = localReactions !== null ? localReactions : (post.reactions || {});
  const totalReactions = Object.values(reactions).reduce((sum, arr) => sum + arr.length, 0);
  // Names for the who-reacted tooltip: backend GET names + any merged in realtime
  // (own optimistic reaction / WS reactor_name) so a fresh reactor never shows "User".
  const reactionNames = { ...(post.reaction_names || {}), ...(localReactionNames || {}) };
  const commentCount = post.comment_count || 0;
  const viewCount = post.view_count || 0;
  // 104 · the mock's post header carries the author's role and an absolute time,
  // and the meta line reads "Seen by n of m" using the member list the client
  // already holds (103 §5b: no new endpoint).
  const memberTotal = Array.isArray(churchMembers) ? churchMembers.length : 0;

  // Fetch read-by details when popover opens
  const fetchReadBy = async () => {
    if (readByData) { setShowReadBy(true); return; }
    setReadByLoading(true);
    setShowReadBy(true);
    try {
      const churchId = user?.church_id || '';
      const res = await fetch(`${API_URL}/api/comms/v3/posts/${post.id}/views?church_id=${encodeURIComponent(churchId)}`);
      if (res.ok) {
        setReadByData(await res.json());
      }
    } catch (err) {
      console.error('Error fetching read-by:', err);
    } finally {
      setReadByLoading(false);
    }
  };

  const resendToUnreads = async () => {
    setResendLoading(true);
    try {
      const churchId = user?.church_id || '';
      const res = await fetch(
        `${API_URL}/api/comms/v3/posts/${post.id}/resend-notification?church_id=${encodeURIComponent(churchId)}&user_id=${encodeURIComponent(user?.id || '')}`,
        { method: 'POST' }
      );
      if (res.ok) {
        const data = await res.json();
        setResendSent(true);
        if (data.notified_count > 0) {
          toast.success(`Notification resent to ${data.notified_count} member${data.notified_count !== 1 ? 's' : ''}`);
        } else {
          toast.info(data.message || 'No members to notify');
        }
      } else {
        toast.error("Couldn't resend notification. Please try again.");
      }
    } catch (err) {
      console.error('Error resending notification:', err);
      toast.error("Couldn't resend notification. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };
  
  // R16 (2026-09-03): a user may hold several reactions on a post.
  const userReactions = Object.entries(reactions)
    .filter(([, users]) => (users || []).includes(user.id))
    .map(([type]) => type);
  const userReaction = userReactions[0];

  // Sync local reactions when post.reactions changes from parent (WebSocket updates)
  // Use JSON.stringify to detect actual content changes, not just reference changes
  const postReactionsKey = JSON.stringify(post.reactions || {});
  useEffect(() => {
    setLocalReactions(null);
  }, [postReactionsKey]);

  // Thread comments: group replies under their parent using depth-first traversal
  const threadedComments = useMemo(() => {
    const childMap = {};
    comments.forEach(c => {
      if (c.parent_comment_id) {
        if (!childMap[c.parent_comment_id]) childMap[c.parent_comment_id] = [];
        childMap[c.parent_comment_id].push(c);
      }
    });
    const result = [];
    const addWithReplies = (comment) => {
      result.push(comment);
      (childMap[comment.id] || []).forEach(addWithReplies);
    };
    comments.filter(c => !c.parent_comment_id).forEach(addWithReplies);
    return result;
  }, [comments]);

  // Comments to display (collapsed = first 2, expanded = all)
  const visibleComments = showAllComments ? threadedComments : threadedComments.slice(0, 2);
  const hiddenCommentCount = comments.length - 2;

  const fetchComments = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/posts/${post.id}/comments?viewer_id=${encodeURIComponent(user?.id || '')}`);
      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  }, [post.id]);

  // Fetch church members for @mentions — uses the shared module cache so
  // a request only ever fires once per church per page session, regardless
  // of how many PostCards are mounted.
  const fetchMembers = useCallback(async () => {
    if (churchMembers !== null || !user?.church_id) return;
    const data = await getMentionMembers(user.church_id);
    // Cache returns null on transient failure; only commit to state on
    // a real result (empty array is fine — that's a loaded result).
    // Leaving state at null lets the next render naturally retry.
    if (data !== null) setChurchMembers(data);
  }, [user?.church_id, churchMembers]);

  // Studio's post header shows the author's role and "Seen by n of m", so the
  // member list is needed before a mention is typed.  Shared module cache: one
  // request per church per page session however many cards are mounted.
  useEffect(() => {
    if (isStudio && churchMembers === null) fetchMembers();
  }, [isStudio, churchMembers, fetchMembers]);

  // Fetch church groups for @mentions — same shared-cache pattern.
  const fetchGroups = useCallback(async () => {
    if (churchGroups !== null || !user?.church_id) return;
    const data = await getMentionGroups(user.church_id);
    if (data !== null) setChurchGroups(data);
  }, [user?.church_id, churchGroups]);

  // Auto-expand and scroll to highlighted comment
  useEffect(() => {
    if (highlightedCommentId && autoExpandComments) {
      setShowComments(true);
      setShowAllComments(true);
      fetchComments();
    }
  }, [highlightedCommentId, autoExpandComments, fetchComments]);

  // D4: the card root, observed so Studio's auto-opened comments fetch on
  // approach instead of on mount. Never read in classic.
  const postRootRef = useRef(null);
  const commentScrolledRef = useRef(null);
  useEffect(() => {
    // Mobile-parity spec: 600ms settle delay, 150px breathing room above the
    // comment (scroll-mt), and a once-guard so it only ever fires once.
    if (highlightedCommentId && commentHighlightRef.current && commentScrolledRef.current !== highlightedCommentId) {
      commentScrolledRef.current = highlightedCommentId;
      setTimeout(() => {
        commentHighlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 600);
    }
  }, [highlightedCommentId, comments]);

  // D4 (121 §5): Studio keeps the auto-expanded look but drops the per-post
  // fetch on mount. `/api/comms/v3/posts` returns comment_count only (never the
  // comments themselves — comms_v3.py builds the array in the single-post and
  // the /comments routes alone), so the look needs a request; it just no longer
  // needs it before the card is anywhere near the viewport. A card Studio
  // opened on its own waits for an IntersectionObserver (rootMargin 200px) and
  // fires at most one request; a post with comment_count 0 is never opened, so
  // it never fetches. A user-opened card (classic, and a Studio click) fetches
  // immediately, exactly as before.
  const studioAutoOpened = isStudio && !autoExpandComments && (post.comment_count || 0) > 0;
  const [commentsNearView, setCommentsNearView] = useState(false);

  useEffect(() => {
    if (!studioAutoOpened || commentsNearView) return undefined;
    const el = postRootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // No observer (old browser / jsdom): behave exactly as before.
      setCommentsNearView(true);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        setCommentsNearView(true);
        io.disconnect();
      }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [studioAutoOpened, commentsNearView]);

  useEffect(() => {
    if (!showComments) return;
    if (studioAutoOpened && !commentsNearView) return;
    fetchComments();
  }, [showComments, fetchComments, studioAutoOpened, commentsNearView]);

  // Listen for real-time comment reaction updates via WebSocket
  useEffect(() => {
    const handleCommentReaction = (event) => {
      const data = event.detail;
      // Only update if this event is for the current post
      if (data.post_id !== post.id) return;
      
      // Update the comment's reactions in local state
      setComments(prev => prev.map(comment => {
        if (comment.id === data.comment_id) {
          // Merge the reactor's name so the "who reacted" tooltip resolves it
          // immediately instead of falling back to "User" until a refetch.
          const mergedNames = { ...(comment.reaction_names || {}) };
          if (data.reactor_name && data.user_id) {
            mergedNames[data.user_id] = data.reactor_name;
          }
          return { ...comment, reactions: data.reactions, reaction_names: mergedNames };
        }
        return comment;
      }));
    };

    window.addEventListener('ws_comment_reaction', handleCommentReaction);
    return () => window.removeEventListener('ws_comment_reaction', handleCommentReaction);
  }, [post.id]);

  // Listen for real-time new comments/replies via WebSocket
  useEffect(() => {
    const handleNewComment = (event) => {
      const data = event.detail;
      // Only update if this event is for the current post
      if (data.post_id !== post.id) return;

      // Skip own comments - they're already added via the API response
      if (data.comment?.user_id === user?.id) return;

      // Add the new comment to local state if it doesn't already exist
      setComments(prev => {
        // Check if comment already exists (avoid duplicates)
        if (prev.some(c => c.id === data.comment.id)) {
          return prev;
        }
        return [...prev, data.comment];
      });
    };

    window.addEventListener('ws_new_comment', handleNewComment);
    return () => window.removeEventListener('ws_new_comment', handleNewComment);
  }, [post.id, user?.id]);

  const handleReaction = async (reactionType) => {
    if (isReacting) return; // Prevent double-taps
    
    setIsReacting(true);
    setShowReactions(false);
    
    // Optimistic update - immediately update local state
    const currentReactions = localReactions !== null ? localReactions : (post.reactions || {});
    const newReactions = { ...currentReactions };
    
    // Check if user already has this reaction
    const hasReaction = (newReactions[reactionType] || []).includes(user.id);
    
    if (hasReaction) {
      // Remove the reaction
      newReactions[reactionType] = (newReactions[reactionType] || []).filter(id => id !== user.id);
      if (newReactions[reactionType].length === 0) {
        delete newReactions[reactionType];
      }
    } else {
      // R16: keep the user's other reactions; only add the tapped one
      if (!newReactions[reactionType]) {
        newReactions[reactionType] = [];
      }
      newReactions[reactionType].push(user.id);
    }
    
    // Apply optimistic update immediately
    setLocalReactions(newReactions);
    // Merge our own name so the who-reacted tooltip resolves it instantly.
    const myName = user?.name || user?.user_metadata?.name;
    if (myName) {
      setLocalReactionNames(prev => ({ ...(prev || {}), [user.id]: myName }));
    }
    
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/posts/${post.id}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, emoji: reactionType })
      });
      
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 422) toast.error("That reaction isn't allowed");
        else if (response.status === 429 && typeof data?.detail === 'string') toast.error(data.detail);
        throw new Error('Failed to save reaction');
      }
      // The server may store a different key than we sent (❤ → love, 👍🏽 → like):
      // move the optimistic entry so one reaction never shows as two chips.
      if (data?.emoji && data.emoji !== reactionType) {
        setLocalReactions((prev) => {
          const next = { ...(prev || newReactions) };
          const mine = (next[reactionType] || []).includes(user.id);
          next[reactionType] = (next[reactionType] || []).filter((id) => id !== user.id);
          if (next[reactionType].length === 0) delete next[reactionType];
          const target = (next[data.emoji] || []).filter((id) => id !== user.id);
          if (mine && data.action !== 'removed') target.push(user.id);
          if (target.length > 0) next[data.emoji] = target; else delete next[data.emoji];
          return next;
        });
      }
      // Don't call onUpdate() - let the optimistic update stand
      // The next full refresh will sync if needed
    } catch (err) {
      console.error('Error reacting:', err);
      // Revert optimistic update on error
      setLocalReactions(null);
    } finally {
      setIsReacting(false);
    }
  };

  // Handle comment reactions — one in flight at a time, like post reactions
  const handleCommentReaction = async (commentId, reactionType) => {
    if (isReacting) return;
    setIsReacting(true);
    try {
      await handleCommentReactionInner(commentId, reactionType);
    } finally {
      setIsReacting(false);
    }
  };
  const handleCommentReactionInner = async (commentId, reactionType) => {
    setCommentReactionPopover(null);
    
    // Optimistic update for comment reactions
    setComments(prev => prev.map(comment => {
      if (comment.id !== commentId) return comment;
      
      const currentReactions = comment.reactions || {};
      const newReactions = { ...currentReactions };
      
      // Check if user already has this reaction
      const hasReaction = (newReactions[reactionType] || []).includes(user.id);
      
      if (hasReaction) {
        // Remove the reaction
        newReactions[reactionType] = (newReactions[reactionType] || []).filter(id => id !== user.id);
        if (newReactions[reactionType].length === 0) {
          delete newReactions[reactionType];
        }
      } else {
        // R16: several reactions per user; only the tapped one is added
        if (!newReactions[reactionType]) {
          newReactions[reactionType] = [];
        }
        newReactions[reactionType].push(user.id);
      }
      
      // Merge our own display name so the "who reacted" tooltip resolves it
      // immediately instead of falling back to "User" until the next refetch.
      const myName = user?.name || user?.user_metadata?.name;
      const mergedNames = { ...(comment.reaction_names || {}) };
      if (myName) mergedNames[user.id] = myName;

      return { ...comment, reactions: newReactions, reaction_names: mergedNames };
    }));
    
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/comments/${commentId}/reactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, emoji: reactionType })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save comment reaction');
        // Could revert optimistic update here if needed
      }
    } catch (err) {
      console.error('Error reacting to comment:', err);
      // Refresh comments to get actual state
      fetchComments();
    }
  };

  // Get user's reaction on a comment
  // R16: all of the user's reactions on a comment (first one drives the label)
  const getUserCommentReactions = (comment) => {
    const reactions = comment.reactions || {};
    return Object.entries(reactions)
      .filter(([, users]) => (users || []).includes(user.id))
      .map(([type]) => type);
  };
  const getUserCommentReaction = (comment) => getUserCommentReactions(comment)[0];

  // Get total reactions on a comment
  const getCommentReactionCount = (comment) => {
    const reactions = comment.reactions || {};
    return Object.values(reactions).reduce((sum, arr) => sum + arr.length, 0);
  };

  // Handle comment input change — detect @mention queries (allows hyphens for group tags)
  const handleCommentChange = (e) => {
    const val = e.target.value;
    setNewComment(val);
    // Detect @mention: find the last @ that isn't preceded by a word char
    const cursor = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursor);
    const atMatch = textBeforeCursor.match(/(^|[^a-zA-Z0-9])@([a-zA-Z0-9-]*)$/);
    if (atMatch) {
      const query = atMatch[2];
      // null = not loaded yet; [] = loaded but empty (don't refetch)
      if (churchMembers === null) fetchMembers();
      if (churchGroups === null) fetchGroups();
      setMentionQuery(query);
    } else {
      setMentionQuery(null);
    }
  };

  // Handle selecting a member or group from the mention dropdown
  const handleMentionSelect = (item) => {
    const input = commentInputRef.current;
    const cursor = input?.selectionStart || newComment.length;
    const textBeforeCursor = newComment.slice(0, cursor);
    const atIdx = textBeforeCursor.lastIndexOf('@');
    if (atIdx === -1) return;
    const before = newComment.slice(0, atIdx);
    const after = newComment.slice(cursor);

    let inserted; // the literal text after '@'
    if (item.kind === 'group') {
      inserted = item.tag;
      setCommentMentions(prev => {
        if (prev.some(m => m.group_id === item.id)) return prev;
        return [...prev, { group_id: item.id, tag: item.tag, name: item.name }];
      });
    } else {
      inserted = item.user_name;
      setCommentMentions(prev => {
        if (prev.some(m => m.user_id === item.user_id)) return prev;
        return [...prev, { user_id: item.user_id, name: item.user_name }];
      });
    }
    setNewComment(`${before}@${inserted} ${after}`);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      if (input) {
        const pos = before.length + 1 + inserted.length + 1;
        input.setSelectionRange(pos, pos);
        input.focus();
      }
    });
  };

  const MAX_COMMENT_IMAGES = 10; // R15: matches server and mobile

  // Upload images attached to a comment. Reuses the same /upload/images
  // endpoint as posts but with prefix=comments so storage paths are
  // attributable. Inherits the backend's 7MB-per-file + image type guards
  // (HEIC is converted to JPEG server-side).
  const uploadCommentImages = async (files) => {
    if (!files || files.length === 0) return;
    const remainingSlots = MAX_COMMENT_IMAGES - commentImages.length;
    if (remainingSlots <= 0) {
      toast.error(`You can attach up to ${MAX_COMMENT_IMAGES} images per comment.`);
      return;
    }
    const filesToUpload = Array.from(files).slice(0, remainingSlots);

    setUploadingCommentImages(true);
    setCommentImageProgress(0);
    try {
      const formData = new FormData();
      filesToUpload.forEach(f => formData.append('files', f));
      const data = await uploadWithProgress(
        `${API_URL}/api/comms/v3/upload/images?user_id=${encodeURIComponent(user.id)}&prefix=comments`,
        formData,
        (pct) => setCommentImageProgress(pct),
        0
      );
      setCommentImages(prev => [...prev, ...(data.images || [])]);
    } catch (err) {
      console.error('Error uploading comment images:', err);
      toast.error(err.message || 'Image upload failed.');
    } finally {
      setUploadingCommentImages(false);
      setCommentImageProgress(null);
      if (commentImageInputRef.current) commentImageInputRef.current.value = '';
    }
  };

  const handleCommentPaste = (e) => {
    if (commentImages.length >= MAX_COMMENT_IMAGES) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const f = items[i].getAsFile();
        if (f && isImageFile(f)) imageFiles.push(f);
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      uploadCommentImages(imageFiles);
    }
  };

  const handleCommentVideoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (commentVideoInputRef.current) commentVideoInputRef.current.value = '';
    if (file.size > 400 * 1024 * 1024) { toast.error('Video must be under 400MB.'); return; }
    try {
      const duration = await new Promise((resolve, reject) => {
        const vid = document.createElement('video');
        vid.preload = 'metadata';
        vid.onloadedmetadata = () => { URL.revokeObjectURL(vid.src); resolve(vid.duration); };
        vid.onerror = () => { URL.revokeObjectURL(vid.src); reject(new Error('Cannot read video')); };
        vid.src = URL.createObjectURL(file);
      });
      if (duration > 240) { toast.error('Video must be under 4 minutes.'); return; }
    } catch { /* server will enforce */ }
    setCommentVideoProgress(0);
    try {
      const data = await uploadVideoWithProgress(
        `${API_URL}/api/comms/v3/upload/video?user_id=${encodeURIComponent(user.id)}&prefix=comments`,
        file,
        (pct) => setCommentVideoProgress(pct)
      );
      setCommentVideo(data.video);
    } catch (err) { console.error('Error uploading comment video:', err); toast.error(err.message || 'Video upload failed.'); }
    finally { setCommentVideoProgress(null); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() && !commentGif && commentImages.length === 0 && !commentVideo) return;
    if (uploadingCommentImages || commentVideoProgress !== null) return;
    try {
      const body = { 
        user_id: user.id, 
        content: newComment,
        parent_comment_id: replyingTo?.commentId || null,
        reply_to_user_id: replyingTo?.userId || null,
        gif_url: commentGif?.url || null,
        media_urls: commentImages.map(img => img.url),
        mentions: commentMentions,
        video: commentVideo || undefined
      };
      const response = await fetch(`${API_URL}/api/comms/v3/posts/${post.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        const newCommentData = await response.json();
        setComments(prev => [...prev, newCommentData]);
        setNewComment('');
        setCommentGif(null);
        setCommentImages([]);
        setCommentVideo(null);
        setCommentMentions([]);
        setMentionQuery(null);
        setReplyingTo(null);
        onUpdate();
      }
    } catch (err) {
      console.error('Error adding comment:', err);
      toast.error("Couldn't post your comment. Please try again.");
    }
  };

  const handleDeleteClick = () => {
    setMenuOpen(false);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteDialog(false);
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/posts/${post.id}?user_id=${user.id}&is_admin=${isAdmin}`, { method: 'DELETE' });
      if (response.ok) {
        onUpdate();
      } else {
        const error = await response.json();
        console.error('Delete failed:', error);
        toast.error('Couldn\'t delete this post. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting:', err);
      toast.error('Couldn\'t delete this post. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await fetch(`${API_URL}/api/comms/v3/posts/${post.id}?user_id=${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          content: editContent,
          remove_gif: removeGif,
          remove_embed: removeEmbed,
          remove_voice_memo: removeVoiceMemo,
          remove_video: removeVideo
        })
      });
      setEditing(false);
      setRemoveGif(false);
      setRemoveEmbed(false);
      setRemoveVoiceMemo(false);
      setRemoveVideo(false);
      onUpdate();
    } catch (err) {
      console.error('Error updating:', err);
    }
  };
  
  const handleCancelEdit = () => {
    setEditing(false);
    setEditContent(post.content);
    setRemoveGif(false);
    setRemoveEmbed(false);
    setRemoveVoiceMemo(false);
    setRemoveVideo(false);
  };

  // Handle pin/unpin post (admin only)
  const handleTogglePin = async () => {
    if (isPinning) return;
    setIsPinning(true);
    setMenuOpen(false);
    
    try {
      const response = await fetch(
        `${API_URL}/api/comms/v3/posts/${post.id}/pin?user_id=${encodeURIComponent(user.id)}&is_admin=${isAdmin}`,
        { method: 'PUT' }
      );
      
      if (response.ok) {
        if (onUpdate) onUpdate();
      } else {
        console.error('Failed to toggle pin');
        toast.error('Couldn\'t pin this post. Please try again.');
      }
    } catch (err) {
      console.error('Error toggling pin:', err);
      toast.error('Couldn\'t pin this post. Please try again.');
    } finally {
      setIsPinning(false);
    }
  };

  return (
    <>
    <Card
      ref={postRootRef}
      /* D20 (121 §5, Option A): the hue, the band and the centred headline are all
         keyed off this one attribute. Studio only — on classic the value is
         `undefined`, so React writes no attribute and the DOM is unchanged. */
      data-sys={isStudio && (isBirthdayPost || isDevotionPost)
        ? (isBirthdayPost ? 'birthday' : 'devotion')
        : undefined}
      className={isStudio
        ? `st-pcard relative border border-border bg-card ${post.is_pinned ? 'st-pinned' : ''}`
        : `shadow-lg shadow-slate-200/50 dark:shadow-none border transition-all duration-300 ${
        isBirthdayPost 
          ? 'bg-gradient-to-br from-amber-50 via-pink-50 to-purple-50 dark:from-pink-950/40 dark:via-purple-950/30 dark:to-amber-950/20 border-pink-200 dark:border-pink-400/40 ring-2 ring-pink-100 dark:ring-pink-500/20' 
          : isDevotionPost
            ? 'bg-gradient-to-br from-sky-50 via-indigo-50 to-blue-50 dark:from-indigo-950/40 dark:via-sky-950/30 dark:to-blue-950/20 border-indigo-200 dark:border-indigo-400/50 ring-2 ring-indigo-100 dark:ring-indigo-400/25'
            : 'border-slate-100 dark:border-white/10 bg-card'
      } hover:shadow-xl ${post.is_pinned ? 'ring-2 ring-blue-200' : ''}`}
    >
      <CardContent className={isStudio ? 'st-pcbody' : "pt-5 pb-4"}>
        {isStudio && isHighlighted && (
          <div className="notif-highlight-ring rounded-xl" aria-hidden="true" />
        )}
        {/* Birthday Banner */}
        {isBirthdayPost && !isStudio && (
          <div className="mb-3">
            <div className="shimmer-once flex items-center justify-center gap-2 text-pink-600 dark:text-pink-300 text-sm font-semibold py-2.5 bg-gradient-to-r from-pink-100/50 via-amber-100/50 to-purple-100/50 dark:from-pink-900/30 dark:via-purple-900/20 dark:to-amber-900/20 rounded-lg border border-pink-200/50 dark:border-pink-400/30">
              <Cake className="w-5 h-5" />
              <span>Birthday Celebration</span>
              <Cake className="w-5 h-5" />
            </div>
            <div className="text-xs text-slate-400 dark:text-slate-500 mt-1.5 text-center" data-testid="post-timestamp-birthday">
              {formatTimeAgo(post.created_at)}
            </div>
          </div>
        )}

        {/* Devotion Banner */}
        {isDevotionPost && !isStudio && (
          <div className="mb-3">
            <div className="flex items-center justify-center gap-2 text-indigo-600 dark:text-indigo-200 text-sm font-semibold py-2.5 bg-gradient-to-r from-indigo-100/50 via-sky-100/50 to-blue-100/50 dark:from-indigo-800/50 dark:via-sky-900/30 dark:to-blue-900/30 rounded-lg border border-indigo-200/50 dark:border-indigo-400/30">
              <BookOpen className="w-5 h-5" />
              <span>Week {post.devo_week || ''} Team Devotion</span>
              <BookOpen className="w-5 h-5" />
            </div>
            <div className="text-xs text-slate-400 dark:text-slate-500 mt-1.5 text-center" data-testid="post-timestamp-devotion">
              {formatTimeAgo(post.created_at)}
            </div>
          </div>
        )}

        {/* Studio: a system post (birthday, weekly devotion) takes the mock's
            header.  D20 (owner, Option A "Quiet celebration", without the time):
            this row becomes the 44px tinted band across the card's top — emoji as
            content, then the eyebrow, which now reads the label **only**
            ("Birthday" / "Week N team devotion").  The timestamp element stays in
            the DOM so `post-timestamp-birthday|devotion` still resolves; §46 hides
            it visually (clipped, never `display: none`).  The separator dot went
            with the time it joined.  The author line stays in the DOM and is hidden
            by §46, exactly as the approved mock drew it.  Delete stays in the right
            cluster for admins. */}
        {isStudio && (isBirthdayPost || isDevotionPost) && (
          <div className="st-ph st-sys">
            <span className="st-emo" aria-hidden="true">{isBirthdayPost ? '🎂' : '📖'}</span>
            <div>
              <div className="text-[14px] font-semibold text-foreground">
                {isBirthdayPost ? (user?.church_name || post.author?.name || 'Birthday') : 'Worship OS'}
              </div>
              <div className="st-pmeta">
                <span>{isBirthdayPost
                  ? 'Birthday'
                  : (post.devo_week ? `Week ${post.devo_week} team devotion` : 'Team devotion')}</span>
                <span data-testid={isBirthdayPost ? 'post-timestamp-birthday' : 'post-timestamp-devotion'}>
                  {studioPostTime(post.created_at)}
                </span>
              </div>
            </div>
            {canDelete && (
              <div className="st-pr">
                <button type="button" className="st-tool st-ic" aria-label="Delete post" title="Delete"
                        onClick={handleDeleteClick} disabled={isDeleting}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Pinned Badge */}
        {post.is_pinned && !isStudio && (
          <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 text-xs mb-2">
            <Pin className="w-3 h-3" />
            <span>Pinned</span>
          </div>
        )}

        {/* Header - Hidden for birthday/devotion posts since they have special banners */}
        {!isBirthdayPost && !isDevotionPost && (
        <div className={isStudio ? 'st-ph' : 'flex items-start justify-between mb-3'}>
          <div className="flex items-center gap-3">
            <UserAvatar 
              userId={post.user_id}
              name={post.author?.name}
              avatarUrl={post.author?.avatar_url}
              size={isStudio ? 'sm' : 'md'}
              className={isStudio ? 'w-9 h-9 text-[13px]' : undefined}
              showBorder={!isStudio}
              showShadow={!isStudio}
            />
            <div>
              <div className={isStudio ? 'text-[14px] font-semibold text-foreground' : 'font-medium text-slate-800 dark:text-slate-100'}>
                {post.author?.name || 'Unknown User'}
              </div>
              <div className={isStudio ? 'st-pmeta' : 'text-xs text-slate-400 dark:text-slate-500'}>
                <span>{isStudio ? studioPostTime(post.created_at) : formatTimeAgo(post.created_at)}</span>
                {isStudio && post.groups?.length > 0 && (
                  <><span>·</span><span data-testid="post-group-badges">to {post.groups.map((g) => g.name || g.tag).join(', ')}</span></>
                )}
              </div>
            </div>
          </div>
          
          <div className={isStudio ? 'st-pr' : 'flex items-center gap-2'}>
            {isStudio && post.is_pinned && (
              <span className="st-pin"><Pin className="w-3.5 h-3.5" />Pinned</span>
            )}
            {/* Group Badges */}
            {!isStudio && post.groups && post.groups.length > 0 && (
              <div className="flex flex-wrap justify-end gap-1" data-testid="post-group-badges">
                {post.groups.map(g => {
                  const color = g.color || '#6366F1';
                  return (
                    <span
                      key={g.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ 
                        backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
                        color: `color-mix(in srgb, ${color} 70%, var(--badge-text-mix))`,
                        border: `1px solid color-mix(in srgb, ${color} 20%, transparent)`,
                      }}
                    >
                      @{g.tag}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Show menu for owner (edit + delete) or admin (delete only) */}
            {(isOwner || canDelete) && (
            <Popover open={menuOpen} onOpenChange={setMenuOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className={isStudio ? 'h-7 w-7' : 'h-8 w-8'} data-testid="post-menu-btn">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className={isStudio ? 'st-fdmenu' : "w-40 p-1"} align="end">
                {/* Pin/Unpin - Admin only */}
                {isAdmin && (
                  <button
                    onClick={handleTogglePin}
                    disabled={isPinning}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-50"
                    data-testid="pin-post-btn"
                  >
                    <Pin className={`w-4 h-4 ${post.is_pinned ? 'text-blue-500 dark:text-blue-400' : ''}`} />
                    {isPinning ? 'Updating...' : post.is_pinned ? 'Unpin' : 'Pin'}
                  </button>
                )}
                {isOwner && (
                  <button
                    onClick={() => { setMenuOpen(false); setEditing(true); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-100 dark:hover:bg-white/10"
                    data-testid="edit-post-btn"
                  >
                    <Edit className="w-4 h-4" /> Edit
                  </button>
                )}
                <button
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-500/15 disabled:opacity-50"
                  data-testid="delete-post-btn"
                >
                  <Trash2 className="w-4 h-4" /> {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </PopoverContent>
            </Popover>
          )}
          </div>
        </div>
        )}

        {/* Admin delete button for birthday/devotion posts */}
        {(isBirthdayPost || isDevotionPost) && canDelete && !isStudio && (
          <div className="flex justify-end mb-2">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-slate-400 dark:text-slate-500 hover:text-red-500 h-7 px-2"
              onClick={handleDeleteClick}
              disabled={isDeleting}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              <span className="text-xs">{isDeleting ? 'Deleting...' : 'Delete'}</span>
            </Button>
          </div>
        )}

        {/* Content */}
        {editing ? (
          <div className={isStudio ? 'st-editwrap' : "mb-3 space-y-3"}>
            <RichTextEditor
              content={editContent}
              onChange={setEditContent}
              placeholder="What's on your mind?"
              minHeight="60px"
              members={churchMembers}
              currentUserId={user?.id}
              onMentionTrigger={() => {
                // Edit mode is editing a POST — only members are mentionable here,
                // not groups (see FeedView post composer for rationale).
                if (churchMembers === null) fetchMembers();
              }}
            />
            
            {/* Editable GIF - show if exists and not marked for removal */}
            {post.gif_url && !removeGif && (
              <div className="relative inline-block">
                <img src={post.gif_url} alt="GIF" className="rounded-lg max-h-40" />
                <button
                  onClick={() => setRemoveGif(true)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white shadow-md transition-colors"
                  title="Remove GIF"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            
            {/* Editable Video Embed - show if exists and not marked for removal */}
            {post.embed && !removeEmbed && (
              <div className="relative">
                <div className="flex items-center gap-2 p-3 bg-slate-100 dark:bg-[#3a3d41] rounded-lg">
                  {post.embed.type === 'youtube' ? (
                    <div className="w-10 h-10 bg-red-600 rounded flex items-center justify-center flex-shrink-0">
                      <Play className="w-5 h-5 text-white" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 bg-purple-600 rounded flex items-center justify-center flex-shrink-0">
                      <Play className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {post.embed.type === 'youtube' ? 'YouTube Video' : 'Loom Video'}
                    </span>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Video ID: {post.embed.video_id}</p>
                  </div>
                  <button
                    onClick={() => setRemoveEmbed(true)}
                    className="w-7 h-7 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors flex-shrink-0"
                    title="Remove video"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            
            {/* Editable Voice Memo - show if exists and not marked for removal */}
            {(post.voice_memo_data || post.voice_memo_id) && !removeVoiceMemo && (
              <div className="relative">
                <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-indigo-50 dark:from-indigo-950/40 to-purple-50 rounded-lg border border-indigo-100">
                  <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mic className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Voice Memo</span>
                    <p className="text-xs text-indigo-500">Audio attached</p>
                  </div>
                  <button
                    onClick={() => setRemoveVoiceMemo(true)}
                    className="w-7 h-7 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors flex-shrink-0"
                    title="Remove voice memo"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Editable Video - show if exists and not marked for removal */}
            {post.video && !removeVideo && (
              <div className="relative inline-block">
                <div className="relative rounded-lg overflow-hidden max-w-[200px] border border-slate-200 dark:border-white/10 bg-black">
                  {post.video.thumbnail_url ? (
                    <img src={post.video.thumbnail_url} alt="Video thumbnail" className="w-full h-auto" />
                  ) : (
                    <div className="w-[200px] h-[120px] flex items-center justify-center">
                      <Play className="w-8 h-8 text-white/40" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-9 h-9 rounded-full bg-black/50 flex items-center justify-center">
                      <Play className="w-4 h-4 text-white ml-0.5" />
                    </div>
                  </div>
                  {post.video.duration ? (
                    <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1 py-0.5 rounded">
                      {Math.floor(post.video.duration / 60)}:{String(Math.floor(post.video.duration % 60)).padStart(2, '0')}
                    </div>
                  ) : null}
                </div>
                <button
                  onClick={() => setRemoveVideo(true)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white shadow-md transition-colors"
                  title="Remove video"
                  data-testid="remove-edit-video-btn"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            
            <div className={isStudio ? 'st-editfoot' : "flex gap-2 pt-2"}>
              <Button size="sm" className={isStudio ? 'st-btn st-pri st-sm' : undefined} onClick={handleSaveEdit}>Save</Button>
              <Button size="sm" variant="ghost" className={isStudio ? 'st-btn st-sm' : undefined} onClick={handleCancelEdit}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div 
            className={`mb-3 post-content-display ${isStudio ? 'studio-post-body text-foreground' : (
              isBirthdayPost ? 'text-center text-lg font-medium text-pink-700 dark:text-slate-100' 
              : isDevotionPost ? 'text-center text-base font-medium text-indigo-700 dark:text-slate-100' 
              : 'text-slate-700 dark:text-slate-200'
            )}`}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(applyMentionStyling(normalizePastedSpaces(
              // Studio's birthday header already carries the cake, so the body does not repeat it.
              (isStudio && isBirthdayPost) ? (post.content || '').replace(/🎂\s*/, '') : post.content
            ), post.mentions)) }}
          />
        )}

        {/* Post Content Display Styles */}
        <style>{`
          /* R43 (2026-09-07): never let a post body overflow its card. Text pasted
             from Mail/Notes/Word arrives with non-breaking spaces, and long
             URLs have no break opportunity at all; both used to run off the
             right edge (worst on the wide desktop window). !important beats a
             pasted inline white-space style. */
          .post-content-display { overflow-wrap: anywhere; word-break: break-word; min-width: 0; max-width: 100%; }
          .post-content-display li, .post-content-display li > p, .post-content-display p { white-space: normal !important; }
          .post-content-display h1 { font-size: 1.5rem; font-weight: 700; margin-bottom: 0.5rem; line-height: 1.3; }
          .post-content-display h2 { font-size: 1.25rem; font-weight: 600; margin-bottom: 0.5rem; line-height: 1.3; }
          .post-content-display h3 { font-size: 1.1rem; font-weight: 600; margin-bottom: 0.5rem; line-height: 1.3; }
          .post-content-display p { margin: 0 0 1em; line-height: 1.5; min-height: 1em; }
          .post-content-display p:last-child { margin-bottom: 0; }
          .post-content-display ul, .post-content-display ol { padding-left: 1.25rem; margin-bottom: 0.5rem; }
          .post-content-display ul { list-style-type: disc; }
          .post-content-display ol { list-style-type: decimal; }
          .post-content-display li { margin-bottom: 0.25rem; }
          .post-content-display strong { font-weight: 600; }
          .post-content-display em { font-style: italic; }
          .post-content-display u { text-decoration: underline; }
          .post-content-display a { color: #2563eb; text-decoration: underline; }
          .post-content-display a:hover { color: #1d4ed8; }
          .mention-tag { color: #2563eb; font-weight: 600; }
          .mention-group { background-color: rgba(99, 102, 241, 0.12); padding: 1px 6px; border-radius: 4px; color: #4f46e5; }
          .dark .post-content-display a { color: #60a5fa; }
          .dark .post-content-display a:hover { color: #93c5fd; }
          .dark .mention-tag { color: #60a5fa; }
          .dark .mention-group { background-color: rgba(129, 140, 248, 0.18); color: #a5b4fc; }
        `}</style>

        {/* GIF */}
        {post.gif_url && (
          <img src={post.gif_url} alt="GIF" className="rounded-lg max-w-full mb-3" />
        )}

        {/* Post Images */}
        {post.media_urls && post.media_urls.length > 0 && (
          <PostImageGallery images={post.media_urls} />
        )}

        {/* YouTube/Loom Embed */}
        {post.embed && (
          <div className="mb-3">
            {post.embed.type === 'youtube' && (
              <div className="aspect-video rounded-lg overflow-hidden bg-slate-900">
                <iframe referrerPolicy="strict-origin-when-cross-origin"
                  src={`https://www.youtube-nocookie.com/embed/${post.embed.video_id}`}
                  className="w-full h-full"
                  allowFullScreen
                  title="YouTube video"
                />
              </div>
            )}
            {post.embed.type === 'loom' && (
              <LoomEmbed 
                videoId={post.embed.video_id} 
                url={post.embed.url}
              />
            )}
          </div>
        )}

        {/* Uploaded Video */}
        {post.video && (
          <div className="mb-3">
            <VideoPlayer
              src={post.video.url}
              thumbnailUrl={post.video.thumbnail_url}
              duration={post.video.duration}
            />
          </div>
        )}

        {/* Voice Memo Player */}
        {(post.voice_memo_data || post.voice_memo_id) && (
          <VoiceMemoPlayer
            audioData={post.voice_memo_id ? `${API_URL}/api/comms/v3/voice-memo/${post.voice_memo_id}` : post.voice_memo_data}
            mimeType={post.voice_memo_mime}
            serverDuration={post.voice_memo_duration}
          />
        )}

        {/* File Attachments */}
        {post.attachments && post.attachments.length > 0 && (
          <div className="space-y-2 mt-2">
            {post.attachments.map((file, index) => {
              const isAudio = file.type && file.type.startsWith('audio/');
              if (isAudio) {
                return (
                  <AudioPlayer
                    key={index}
                    src={file.url}
                    filename={file.filename}
                  />
                );
              }
              if (isStudio) {
                return (
                  <button key={index} type="button" className="st-devo st-file"
                          onClick={() => setViewerFile(file)} data-testid={`file-attachment-${index}`}>
                    <span className="st-cover"><FileText /></span>
                    <div>
                      <b>{file.filename || file.name || 'Attachment'}</b>
                      <span>{file.size ? `${(file.size / 1024).toFixed(0)} KB` : (file.meta || 'File')}</span>
                    </div>
                    <span className="st-btn st-sec st-sm"><Download />Open</span>
                  </button>
                );
              }
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => setViewerFile(file)}
                  className="w-full text-left flex items-center gap-3 px-3 py-2.5 bg-slate-50 dark:bg-[#1e1f22] hover:bg-slate-100 dark:hover:bg-white/10 rounded-lg border border-slate-200 dark:border-white/10 transition-colors group"
                  data-testid={`file-attachment-${index}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-slate-200 dark:bg-[#3e4145] group-hover:bg-slate-300 dark:group-hover:bg-white/15 flex items-center justify-center text-slate-500 dark:text-slate-400 flex-shrink-0 transition-colors">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{file.filename}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500">{file.size ? `${(file.size / 1024).toFixed(0)} KB` : ''}</p>
                  </div>
                  <Download className="w-4 h-4 text-slate-400 dark:text-slate-500 group-hover:text-slate-600 flex-shrink-0 transition-colors" />
                </button>
              );
            })}
          </div>
        )}

        {/* Poll Display */}
        {post.poll && (
          <PollDisplay post={post} user={user} onUpdate={onUpdate} />
        )}

        {/* Devotion Download — Studio draws the mock's attachment card.
            The PDF lives on Google Drive as a full /view page, which refuses to be
            framed and, inside the desktop shell's WebView2, carries that webview's
            Google session ("You need access"). Open it in a new tab like the
            dashboard widget does — the Tauri shell sends window.open to the
            system browser. Uploaded attachments still use AttachmentViewer. */}
        {isStudio && isDevotionPost && post.devo_pdf_url && (
          <div className="st-devo">
            <span className="st-cover"><BookOpen /></span>
            <div>
              <b>This week&apos;s devotion</b>
              <span>Week {post.devo_week || ''} · PDF</span>
            </div>
            <button
              type="button"
              className="st-btn st-sec st-sm"
              onClick={() => window.open(post.devo_pdf_url, '_blank', 'noopener,noreferrer')}
              data-testid="devo-download-btn"
            >
              <Download />Download
            </button>
          </div>
        )}
        {/* Devotion Download Button */}
        {isDevotionPost && post.devo_pdf_url && !isStudio && (
          <div className="mt-3 mb-1 flex justify-center">
            <button
              type="button"
              onClick={() => window.open(post.devo_pdf_url, '_blank', 'noopener,noreferrer')}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
              data-testid="devo-download-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Download This Week's Devo
            </button>
          </div>
        )}

        {viewerFile && (
          <AttachmentViewer file={viewerFile} onClose={() => setViewerFile(null)} />
        )}

        {/* Reactions, Read By & Comments Count */}
        <div className={isStudio ? 'st-pf' : 'flex items-center justify-between py-2 border-t border-b border-slate-100 dark:border-white/10 text-sm text-slate-500 dark:text-slate-400'}>
          <div className={isStudio ? 'st-reacts' : 'flex items-center gap-1'}>
            {totalReactions > 0 && (
              <>
                <TooltipProvider delayDuration={200}>
                  <span className={isStudio ? 'st-reacts' : 'flex -space-x-1'}>
                    {Object.entries(reactions).filter(([_, users]) => users.length > 0).slice(0, 3).map(([type, users]) => (
                      <Tooltip key={type}>
                        <TooltipTrigger asChild>
                          <button className={isStudio ? 'st-react' : 'text-base cursor-default'} data-testid={`post-reaction-${type}-${post.id}`}>
                            {getReactionEmoji(type)}{isStudio && <span className="st-num">{users.length}</span>}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs">
                          <p>{users.map(uid => reactionNames?.[uid] || 'User').join(', ')}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </span>
                </TooltipProvider>
                {!isStudio && <span>{totalReactions}</span>}
              </>
            )}
          </div>
          <div className={isStudio ? 'st-seen' : 'flex items-center gap-3'}>
            {/* Read By indicator */}
            <Popover open={showReadBy} onOpenChange={setShowReadBy}>
              <PopoverTrigger asChild>
                <button
                  onClick={fetchReadBy}
                  className="flex items-center gap-1 hover:text-slate-700 dark:hover:text-slate-100 transition-colors"
                  data-testid={`read-by-btn-${post.id}`}
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span>{isStudio ? `Seen by ${viewCount}${memberTotal ? ` of ${memberTotal}` : ''}` : viewCount}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className={isStudio ? 'st-readby' : "w-64 p-0"} align="end">
                <div className="max-h-72 overflow-y-auto">
                  {readByLoading ? (
                    <div className="p-4 space-y-3">
                      {[0, 1].map((i) => (
                        <div key={i} className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-slate-200/70 dark:bg-white/10 animate-pulse" />
                          <div className="flex-1 space-y-1.5">
                            <div className="h-3 w-24 rounded bg-slate-200/70 dark:bg-white/10 animate-pulse" />
                            <div className="h-3 w-40 rounded bg-slate-200/50 dark:bg-white/5 animate-pulse" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : readByData ? (
                    <>
                      {readByData.seen.length > 0 && (
                        <div>
                          <div className="px-3 py-2 bg-slate-50 dark:bg-[#1e1f22] border-b border-slate-100 dark:border-white/10 rounded-t-xl">
                            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Seen ({readByData.seen.length})</span>
                          </div>
                          {readByData.seen.map((m) => (
                            <div key={m.user_id} className="flex items-center gap-2 px-3 py-1.5">
                              <div className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
                              <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{m.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {readByData.not_seen.length > 0 && (
                        <div>
                          <div className={`px-3 py-2 bg-slate-50 dark:bg-[#1e1f22] border-b border-slate-100 dark:border-white/10 flex items-center justify-between ${readByData.seen.length > 0 ? 'border-t' : 'rounded-t-xl'}`}>
                            <span className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide">Not yet seen ({readByData.not_seen.length})</span>
                            {isAdmin && (
                              <button
                                onClick={resendToUnreads}
                                disabled={resendSent || resendLoading}
                                className={`text-xs font-medium px-2 py-0.5 rounded transition-colors ${
                                  resendSent
                                    ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/40'
                                    : 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/15 active:bg-blue-100'
                                }`}
                                data-testid="resend-to-unreads-btn"
                              >
                                {resendLoading ? 'Sending...' : resendSent ? 'Sent' : 'Resend to unreads'}
                              </button>
                            )}
                          </div>
                          {readByData.not_seen.map((m) => (
                            <div key={m.user_id} className="flex items-center gap-2 px-3 py-1.5">
                              <div className="w-2 h-2 rounded-full bg-slate-300 flex-shrink-0" />
                              <span className="text-sm text-slate-400 dark:text-slate-500 truncate">{m.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="p-4 text-center text-sm text-slate-400 dark:text-slate-500">No data</div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            {(!isStudio || (post.comment_count || 0) > 0) && (
            <button 
              onClick={() => setShowComments(!showComments)}
              className="hover:underline"
            >
              {(() => {
                const n = post.comment_count || 0;
                return isStudio ? `${n} comment${n === 1 ? '' : 's'}` : `${n} comments`;
              })()}
            </button>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className={isStudio ? 'st-pa' : 'flex items-center gap-2 pt-3'}>
          <Popover open={showReactions} onOpenChange={setShowReactions}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className={isStudio
                ? `st-tool${userReaction ? ' st-on' : ''}`
                : `flex-1 h-10 rounded-xl font-medium transition-all ${userReaction ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100' : 'hover:bg-slate-100 dark:hover:bg-white/10'}`}>
                <span key={userReactions.join(',') || 'none'} className={userReaction ? 'like-pop' : undefined}>
                  {isStudio
                    ? <Heart className="w-4 h-4" />
                    : (userReaction ? userReactions.slice(0, 3).map(getReactionEmoji).join('') : '👍')}
                </span> 
                <span className={isStudio ? undefined : 'ml-1.5'}>{isStudio ? 'React' : (userReactions.length > 1 ? `${userReactions.length} reactions` : userReaction ? (isLegacyReactionKey(userReaction) ? userReaction.charAt(0).toUpperCase() + userReaction.slice(1) : 'Reacted') : 'Like')}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className={isStudio ? 'st-rxpop w-auto' : "w-auto p-2 rounded-2xl shadow-xl border-slate-200 dark:border-white/10"} side="top" collisionPadding={12}>
              {/* Quick row + "more" → any emoji (2026-09-09); a picked emoji is sent as its own key */}
              <ReactionPicker
                options={REACTION_OPTIONS}
                selectedKeys={userReactions}
                onSelect={handleReaction}
                testIdPrefix={`post-react-${post.id}`}
              />
            </PopoverContent>
          </Popover>
          <Button 
            variant="ghost" 
            size="sm" 
            className={isStudio
              ? 'st-tool'
              : 'flex-1 h-10 rounded-xl font-medium hover:bg-slate-100 dark:hover:bg-white/10 transition-all'}
            onClick={() => {
              const willShowComments = !showComments;
              setShowComments(willShowComments);
              // Auto-focus the comment input when opening comments
              if (willShowComments) {
                setTimeout(() => commentInputRef.current?.focus(), 100);
              }
            }}
          >
            <MessageSquare className="w-4 h-4 mr-1.5" /> 
            Comment
            {!isStudio && commentCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-[#3a3d41] text-slate-600 dark:text-slate-300 text-xs">{commentCount}</span>
            )}
          </Button>
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className={isStudio ? 'st-cmtwrap fade-up-sm' : "fade-up-sm mt-4 pt-4 border-t border-slate-100 dark:border-white/10"}>
            {/* View more comments link */}
            {comments.length > 2 && !showAllComments && (
              <button
                onClick={() => setShowAllComments(true)}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-200 font-medium mb-3"
              >
                View {hiddenCommentCount} more comment{hiddenCommentCount > 1 ? 's' : ''}
              </button>
            )}
            
            {/* Collapse link when expanded */}
            {showAllComments && comments.length > 2 && (
              <button
                onClick={() => setShowAllComments(false)}
                className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 font-medium mb-3"
              >
                Hide comments
              </button>
            )}
            
            <div className="space-y-3 mb-3">
              {visibleComments.map((comment) => {
                const userCommentReaction = getUserCommentReaction(comment);
                const userCommentReactions = getUserCommentReactions(comment);
                const commentReactionCount = getCommentReactionCount(comment);
                const commentReactions = comment.reactions || {};
                const isReply = !!comment.parent_comment_id;
                const parentComment = isReply ? comments.find(c => c.id === comment.parent_comment_id) : null;
                const replyToName = parentComment?.author?.name || 'someone';
                
                return (
                  <div 
                    key={comment.id} 
                    ref={comment.id === highlightedCommentId ? commentHighlightRef : null}
                    className={isStudio
                      ? `scroll-mt-[150px] st-cmtrow${isReply ? ' st-cmtreply' : ''}`
                      : `scroll-mt-[150px] flex gap-2 ${isReply ? 'ml-8 sm:ml-12' : ''}`}
                  >
                    <UserAvatar 
                      userId={comment.user_id}
                      name={comment.author?.name}
                      avatarUrl={comment.author?.avatar_url}
                      size="sm"
                      className={isStudio ? 'w-7 h-7 text-[11px]' : undefined}
                      showBorder={!isStudio}
                      showShadow={!isStudio}
                    />
                    <div className="flex-1">
                      <div className={isStudio ? 'st-cmtb' : "bg-slate-50 dark:bg-[#1e1f22] rounded-xl px-3 py-2 relative"}>
                        {comment.id === highlightedCommentId && <div className="notif-highlight-ring rounded-xl" aria-hidden="true" />}
                        <div className="font-medium text-sm text-slate-800 dark:text-slate-100">
                          {comment.author?.name || 'Unknown'}
                          <span className="font-normal text-xs text-slate-400 dark:text-slate-500 ml-2">{formatTimeAgo(comment.created_at)}</span>
                        </div>
                        {isReply && (
                          <p className="text-xs text-blue-500 dark:text-blue-400 mb-1">↳ Replying to {replyToName}</p>
                        )}
                        <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap break-words [overflow-wrap:anywhere]" dangerouslySetInnerHTML={{ __html: sanitizeHtml(applyMentionStyling(escapeHtml(comment.content), comment.mentions)) }} />
                        
                        {/* Comment GIF */}
                        {comment.gif_url && (
                          <img 
                            src={comment.gif_url} 
                            alt="GIF" 
                            className="rounded-lg max-w-[200px] max-h-32 mt-1.5 object-cover" 
                            loading="lazy"
                          />
                        )}

                        {/* Comment image attachments */}
                        {comment.media_urls && comment.media_urls.length > 0 && (
                          <div className={`mt-1.5 grid gap-1 ${comment.media_urls.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} max-w-[280px]`}>
                            {comment.media_urls.filter((u) => /^https?:\/\//i.test(u || '')).map((url, idx) => (
                              <a
                                key={`${url}-${idx}`}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                              >
                                <img
                                  src={url}
                                  alt=""
                                  className="rounded-lg w-full max-h-40 object-cover border border-slate-200 dark:border-white/10 hover:opacity-90 transition-opacity"
                                  loading="lazy"
                                />
                              </a>
                            ))}
                          </div>
                        )}

                        {/* Comment video */}
                        {comment.video && (
                          <div className="mt-1.5 max-w-[280px]">
                            <VideoPlayer
                              src={comment.video.url}
                              thumbnailUrl={comment.video.thumbnail_url}
                              duration={comment.video.duration}
                              compact
                            />
                          </div>
                        )}
                        
                        {/* Comment reaction display */}
                        {commentReactionCount > 0 && (
                          <div className="absolute -bottom-2 right-2 flex items-center gap-0.5 bg-card rounded-full px-1.5 py-0.5 shadow-sm border border-slate-100 dark:border-white/10">
                            <TooltipProvider delayDuration={200}>
                              {Object.entries(commentReactions).filter(([, users]) => users.length > 0).slice(0, 3).map(([type, users]) => (
                                users.length > 0 && (
                                  <Tooltip key={type}>
                                    <TooltipTrigger asChild>
                                      <span className="text-xs cursor-default">
                                        {getReactionEmoji(type)}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="max-w-xs">
                                      <p>{users.map(uid => comment.reaction_names?.[uid] || 'User').join(', ')}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )
                              ))}
                            </TooltipProvider>
                            {commentReactionCount > 1 && (
                              <span className="text-xs text-slate-500 dark:text-slate-400 ml-0.5">{commentReactionCount}</span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Comment actions: React and Reply */}
                      <div className={isStudio ? 'st-cmtacts' : "flex items-center gap-3 ml-3 mt-1"}>
                        <Popover open={commentReactionPopover === comment.id} onOpenChange={(open) => setCommentReactionPopover(open ? comment.id : null)}>
                          <PopoverTrigger asChild>
                            <button 
                              className={isStudio
                                ? `st-cmtact${userCommentReaction ? ' st-on' : ''}`
                                : `text-xs transition-colors ${userCommentReaction ? 'text-blue-500 dark:text-blue-400 font-medium' : 'text-slate-400 dark:text-slate-500 hover:text-blue-500'}`}
                            >
                              {userCommentReaction ? `${getReactionEmoji(userCommentReaction)} ` : (isStudio ? '' : '👍 ')}
                              {isStudio && userCommentReaction
                                ? `Liked${commentReactionCount > 1 ? ` · ${commentReactionCount}` : ''}`
                                : 'Like'}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className={isStudio ? 'st-rxpop w-auto' : "w-auto p-1.5 rounded-xl shadow-lg border-slate-200 dark:border-white/10"} side="top" align="start" collisionPadding={12}>
                            <ReactionPicker
                              size="sm"
                              options={REACTION_OPTIONS}
                              selectedKeys={userCommentReactions}
                              onSelect={(key) => handleCommentReaction(comment.id, key)}
                              testIdPrefix={`comment-react-${comment.id}`}
                            />
                          </PopoverContent>
                        </Popover>
                        
                        <button
                          onClick={() => {
                            setReplyingTo({ 
                              commentId: comment.id, 
                              userId: comment.user_id, 
                              userName: comment.author?.name || 'Unknown' 
                            });
                            // Auto-focus the comment input
                            setTimeout(() => commentInputRef.current?.focus(), 100);
                          }}
                          className={isStudio ? 'st-cmtact' : "text-xs text-slate-400 dark:text-slate-500 hover:text-blue-500"}
                        >
                          Reply
                        </button>
                        
                        {/* Delete button - visible for comment owner or admin */}
                        {(comment.user_id === user.id || isAdmin) && (
                          <button
                            onClick={() => {
                              setCommentToDelete(comment);
                              setShowDeleteCommentDialog(true);
                            }}
                            className={isStudio ? 'st-cmtact st-bad' : "text-xs text-slate-400 dark:text-slate-500 hover:text-red-500"}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {replyingTo && (
              <div className={isStudio ? 'st-cmtreplybar' : "flex items-center gap-2 mb-2 p-2 bg-blue-50 dark:bg-blue-950/40 rounded-lg"}>
                <span className={isStudio ? undefined : "text-xs text-blue-600 dark:text-blue-400"}>Replying to {replyingTo.userName}</span>
                <button onClick={() => { setReplyingTo(null); setCommentGif(null); setCommentImages([]); }} className="text-blue-400 hover:text-blue-600">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {/* Comment GIF preview */}
            {commentGif && (
              <div className="mb-2 ml-10" data-testid="comment-gif-preview">
                <div className="relative inline-block">
                  <img
                    src={commentGif.preview || commentGif.url}
                    alt={commentGif.title || 'GIF'}
                    className="h-20 rounded-lg object-cover border border-slate-200 dark:border-white/10"
                  />
                  <button
                    onClick={() => setCommentGif(null)}
                    className="absolute -top-1.5 -right-1.5 bg-slate-700 hover:bg-slate-900 text-white rounded-full p-0.5 shadow-sm transition-colors"
                    data-testid="remove-comment-gif-btn"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
            {/* Comment image previews */}
            {commentImages.length > 0 && (
              <div className="mb-2 ml-10 flex flex-wrap gap-2" data-testid="comment-image-previews">
                {commentImages.map((img, idx) => (
                  <div key={`${img.url}-${idx}`} className="relative inline-block">
                    <img
                      src={img.url}
                      alt={img.filename || 'Image'}
                      className="h-20 w-20 rounded-lg object-cover border border-slate-200 dark:border-white/10"
                    />
                    <button
                      onClick={() => setCommentImages(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute -top-1.5 -right-1.5 bg-slate-700 hover:bg-slate-900 text-white rounded-full p-0.5 shadow-sm transition-colors"
                      data-testid={`remove-comment-image-${idx}-btn`}
                      aria-label="Remove image"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Comment video preview chip — thumbnail style matching image chips */}
            {commentVideo && (
              <div className="mb-2 ml-10 flex flex-wrap gap-2">
                <div className="relative inline-block">
                  <div className="h-20 w-20 rounded-lg overflow-hidden border border-slate-200 dark:border-white/10 bg-black">
                    {commentVideo.thumbnail_url ? (
                      <img src={commentVideo.thumbnail_url} alt="Video" className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-slate-800">
                        <Play className="w-5 h-5 text-white/50" />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-7 h-7 rounded-full bg-black/50 flex items-center justify-center">
                        <Play className="w-3.5 h-3.5 text-white ml-0.5" />
                      </div>
                    </div>
                    <div className="absolute bottom-0.5 right-0.5 bg-black/70 text-white text-[8px] px-1 rounded">
                      {Math.floor((commentVideo.duration || 0) / 60)}:{String(Math.floor((commentVideo.duration || 0) % 60)).padStart(2, '0')}
                    </div>
                  </div>
                  <button
                    onClick={() => setCommentVideo(null)}
                    className="absolute -top-1.5 -right-1.5 bg-slate-700 hover:bg-slate-900 text-white rounded-full p-0.5 shadow-sm transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
            <div className={isStudio ? 'st-cmtin' : 'flex gap-2'}>
              <UserAvatar 
                userId={user?.id}
                name={user?.name || user?.user_metadata?.name}
                avatarUrl={user?.avatar}
                size="sm"
                showBorder={!isStudio}
                showShadow={!isStudio}
              />
              <div className={isStudio ? 'st-cmtfield' : 'flex-1 relative'}>
                {mentionQuery !== null && user?.church_id && (
                  <MentionSuggestions
                    ref={mentionSuggestionsRef}
                    query={mentionQuery}
                    members={churchMembers}
                    groups={churchGroups}
                    loading={churchMembers === null && churchGroups === null}
                    onSelect={handleMentionSelect}
                    currentUserId={user.id}
                  />
                )}
                <Input
                  ref={commentInputRef}
                  placeholder={replyingTo ? `Reply to ${replyingTo.userName}...` : (isStudio ? 'Write a comment…' : "Write a comment... (@ to mention)")}
                  value={newComment}
                  onChange={handleCommentChange}
                  onPaste={handleCommentPaste}
                  onKeyDown={(e) => {
                    if (mentionQuery !== null && mentionSuggestionsRef.current?.handleKeyDown(e)) return;
                    if (e.key === 'Escape' && mentionQuery !== null) { setMentionQuery(null); return; }
                    if (e.key === 'Enter' && !e.shiftKey) handleAddComment();
                  }}
                  className={isStudio ? 'st-cmtinput' : 'bg-slate-50 dark:bg-[#1e1f22] pr-24'}
                  data-testid="comment-input"
                />
                <input
                  ref={commentImageInputRef}
                  type="file"
                  accept={IMAGE_ACCEPT}
                  multiple
                  className="hidden"
                  onChange={(e) => uploadCommentImages(e.target.files)}
                />
                <input
                  ref={commentVideoInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
                  className="hidden"
                  onChange={handleCommentVideoSelect}
                />
                <div className="absolute right-[60px] top-1/2 -translate-y-1/2">
                  <EmojiPickerButton
                    title="Insert emoji"
                    side="top"
                    className="text-slate-400 dark:text-slate-500 hover:text-yellow-500 transition-colors inline-flex items-center"
                    onEmojiSelect={(emoji) => {
                      const el = commentInputRef.current;
                      const start = el?.selectionStart ?? newComment.length;
                      const end = el?.selectionEnd ?? start;
                      const next = newComment.slice(0, start) + emoji + newComment.slice(end);
                      setNewComment(next);
                      requestAnimationFrame(() => {
                        if (el) { el.focus(); const p = start + emoji.length; el.setSelectionRange(p, p); }
                      });
                    }}
                  />
                </div>
                <div className="absolute right-9 top-1/2 -translate-y-1/2">
                  <AttachmentMenu
                    testIdPrefix="comment"
                    size="sm"
                    align="end"
                    showFile={false}
                    triggerClassName="h-auto w-auto p-0 hover:text-indigo-500 dark:hover:text-indigo-400 hover:bg-transparent"
                    onImageClick={() => commentImageInputRef.current?.click()}
                    onVideoClick={() => commentVideoInputRef.current?.click()}
                    imageDisabled={commentImages.length >= MAX_COMMENT_IMAGES || uploadingCommentImages}
                    videoDisabled={!!commentVideo || commentVideoProgress !== null}
                    uploadProgress={
                      commentImageProgress !== null ? commentImageProgress
                      : commentVideoProgress !== null ? commentVideoProgress
                      : null
                    }
                    activeUploadLabel={
                      commentVideoProgress !== null ? 'video'
                      : commentImageProgress !== null ? 'image'
                      : ''
                    }
                  />
                </div>
                <button
                  onClick={() => setShowCommentGifPicker(true)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-yellow-500 transition-colors"
                  title="Add a GIF"
                  data-testid="comment-gif-btn"
                >
                  <span className="text-[8px] font-bold tracking-wide border-[1.5px] border-current rounded px-0.5 leading-tight">GIF</span>
                </button>
              </div>
              <Button size="icon" variant={isStudio ? 'ghost' : undefined} className={isStudio ? 'st-cmtsend' : undefined} onClick={handleAddComment} disabled={(!newComment.trim() && !commentGif && commentImages.length === 0 && !commentVideo) || uploadingCommentImages || commentVideoProgress !== null} data-testid="send-comment-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        <GifPickerDialog
          open={showCommentGifPicker}
          onOpenChange={setShowCommentGifPicker}
          onSelect={(gif) => {
            setCommentGif(gif);
            setShowCommentGifPicker(false);
            requestAnimationFrame(() => commentInputRef.current?.focus());
          }}
        />
      </CardContent>
    </Card>

    {/* Delete Confirmation Dialog */}
    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Post</AlertDialogTitle>
          <AlertDialogDescription>
            {isOwner 
              ? 'Are you sure you want to delete this post? This action cannot be undone.' 
              : 'Are you sure you want to delete this post from another user? This action cannot be undone.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirmDelete}
            className="bg-red-600 hover:bg-red-700"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={showDeleteCommentDialog} onOpenChange={setShowDeleteCommentDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Comment?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this comment? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setCommentToDelete(null)}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 hover:bg-red-700"
            data-testid="confirm-delete-comment-btn"
            onClick={async () => {
              if (!commentToDelete) return;
              try {
                const response = await fetch(
                  `${API_URL}/api/comms/v3/comments/${commentToDelete.id}?user_id=${user.id}&is_admin=${isAdmin}`,
                  { method: 'DELETE' }
                );
                if (response.ok) {
                  setComments(prev => prev.filter(c => c.id !== commentToDelete.id && c.parent_comment_id !== commentToDelete.id));
                  onUpdate();
                }
              } catch (err) {
                console.error('Error deleting comment:', err);
                toast.error("Couldn't delete this comment. Please try again.");
              } finally {
                setCommentToDelete(null);
              }
            }}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

export default PostCard;
